import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as orderAPI from './orderAPI';

export const getAllOrdersAsync = createAsyncThunk(
  'orders/getAllOrders',
  async (type) => {
    const response = await orderAPI.getAllOrders(type);
    return { orders: response, type };
  }
);

export const acceptOrderAsync = createAsyncThunk(
  'orders/acceptOrder',
  async (orderId) => {
    const response = await orderAPI.acceptOrder(orderId);
    return response;
  }
);

export const rejectOrderAsync = createAsyncThunk(
  'orders/rejectOrder',
  async (orderId) => {
    const response = await orderAPI.rejectOrder(orderId);
    return orderId;
  }
);

export const makeOrderAsync = createAsyncThunk(
  'orders/makeOrder',
  async ({type, orderData}) => {
    const response = await orderAPI.makeOrder(type, orderData);
    return response;
  }
);

export const fetchOrderByIdAsync = createAsyncThunk(
  'orders/fetchById',
  async (orderId) => {
    const response = await orderAPI.fetchOrderById(orderId);
    return response;
  }
);

export const acceptCartAsync = createAsyncThunk(
  'orders/acceptCart',
  async (orderId) => {
    const response = await orderAPI.acceptCart(orderId);
    return response;
  }
);

export const updateCartItemAsync = createAsyncThunk(
  'orders/updateCartItem',
  async ({orderId, quantity}) => {
    const response = await orderAPI.updateCartItem(orderId, quantity);
    return response;
  }
);

export const removeCartItemAsync = createAsyncThunk(
  'orders/removeCartItem',
  async (orderId) => {
    await orderAPI.removeCartItem(orderId);
    return orderId;
  }
);

const initialState = {
  cartOrders: [],
  pendingOrders: [],
  approvedOrders: [],
  rejectedOrders: [],
  selectedOrder: null,
  loading: false,
  error: null,
  filterUsername: ''
};

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setSelectedOrder: (state, action) => {
      state.selectedOrder = action.payload;
    },
    clearSelectedOrder: (state) => {
      state.selectedOrder = null;
    },
    setFilterUsername: (state, action) => {
      state.filterUsername = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get All Orders
      .addCase(getAllOrdersAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllOrdersAsync.fulfilled, (state, action) => {
        state.loading = false;
        switch(action.payload.type.toUpperCase()) {
          case 'CART':
            state.cartOrders = action.payload.orders;
            break;
          case 'PENDING':
            state.pendingOrders = action.payload.orders;
            break;
          case 'APPROVED':
            state.approvedOrders = action.payload.orders;
            break;
          case 'REJECTED':
            state.rejectedOrders = action.payload.orders;
            break;
        }
      })
      .addCase(getAllOrdersAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch Order By Id
      .addCase(fetchOrderByIdAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrder = action.payload;
      })
      .addCase(fetchOrderByIdAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Accept Cart
      .addCase(acceptCartAsync.fulfilled, (state, action) => {
        state.cartOrders = state.cartOrders.filter(
          order => order.orderId !== action.payload.orderId
        );
        state.pendingOrders.push(action.payload);
      })
      // Accept Order
      .addCase(acceptOrderAsync.fulfilled, (state, action) => {
        state.pendingOrders = state.pendingOrders.filter(
          order => order.orderId !== action.payload.orderId
        );
        state.approvedOrders.push(action.payload);
      })
      // Reject Order
      .addCase(rejectOrderAsync.fulfilled, (state, action) => {
        state.pendingOrders = state.pendingOrders.filter(
          order => order.orderId !== action.payload
        );
        const rejectedOrder = state.pendingOrders.find(
          order => order.orderId === action.payload
        );
        if (rejectedOrder) {
          state.rejectedOrders.push({...rejectedOrder, status: 'REJECTED'});
        }
      })
      .addCase(updateCartItemAsync.fulfilled, (state, action) => {
        const updatedItem = action.payload;
        const index = state.cartOrders.findIndex(item => item.orderId === updatedItem.orderId);
        if (index !== -1) {
          state.cartOrders[index] = updatedItem;
        }
      })
      .addCase(removeCartItemAsync.fulfilled, (state, action) => {
        state.cartOrders = state.cartOrders.filter(item => item.orderId !== action.payload);
      });
  }
});

export const { 
  setSelectedOrder, 
  clearSelectedOrder, 
  setFilterUsername 
} = orderSlice.actions;

export default orderSlice.reducer;